import {
  ALIGNMENT_AXES,
  alignmentBand,
  breathPatternLabel,
  COACHING_MANTRA_INTENTION_LABELS,
  type AlignmentAxis,
  type CoachingMantraIntention,
  type AlignmentBand,
  type AlignmentSnapshot,
  type StreakState,
} from '@mlm/shared/coaching';
import type { BreathworkPattern, FrequencyTrack, Mantra, Meditation } from './api';
import { durationLabel } from './format';

/**
 * Home's derived pieces (the 2026-10 "Daylight cards" redesign): which tools
 * the reading recommends, the week of streak dots, and where the community
 * link opens. Pure, so the picks can be tested without a screen.
 */

/** The day in a phrase, from the overall band: the Align hub's line and the reading card's headline. */
export function dayLine(overall: number): string {
  switch (alignmentBand(overall)) {
    case 'Strong':
      return 'A strong day for focused action';
    case 'Supportive':
    case 'Balanced':
      return 'A steady day';
    default:
      return 'A day to move thoughtfully';
  }
}

/** The reading card's headline: the day, then the axis to lead with (the first wins a tie). */
export function readingHeadline(alignment: AlignmentSnapshot): string {
  let lead: (typeof ALIGNMENT_AXES)[number] = ALIGNMENT_AXES[0];
  for (const axis of ALIGNMENT_AXES) {
    if (alignment[axis.key] > alignment[lead.key]) lead = axis;
  }
  return `${dayLine(alignment.overall)}. Lead with ${lead.label}.`;
}

export type SoftAxis = { key: AlignmentAxis; label: string; value: number; band: AlignmentBand };

/** The day's lowest axis: the one the "Tools for today" rail is picked for. The first wins a tie. */
export function softestAxis(alignment: AlignmentSnapshot): SoftAxis {
  let pick: (typeof ALIGNMENT_AXES)[number] = ALIGNMENT_AXES[0];
  for (const axis of ALIGNMENT_AXES) {
    if (alignment[axis.key] < alignment[pick.key]) pick = axis;
  }
  const value = alignment[pick.key];
  return { key: pick.key, label: pick.label, value, band: alignmentBand(value) };
}

/**
 * What each axis asks of a tool, most fitting first: a breathwork outcome, a
 * frequency category, a mantra or meditation intention. A tenant's own
 * categories fall through to the first item of each list.
 */
const TOOL_FIT: Record<
  AlignmentAxis,
  { breath: string[]; frequency: string[]; mantra: string[]; meditation: string[] }
> = {
  business: {
    breath: ['focus', 'energy'],
    frequency: ['focus', 'confidence'],
    mantra: ['beginnings', 'clarity'],
    meditation: ['focus', 'confidence'],
  },
  money: {
    breath: ['manifestation', 'confidence'],
    frequency: ['abundance'],
    mantra: ['abundance'],
    meditation: ['abundance'],
  },
  focus: {
    breath: ['focus', 'calm'],
    frequency: ['focus', 'meditation'],
    mantra: ['clarity', 'peace'],
    meditation: ['focus', 'calm'],
  },
  relationships: {
    breath: ['calm', 'confidence'],
    frequency: ['relaxation', 'spiritual'],
    mantra: ['goodwill', 'peace'],
    meditation: ['gratitude', 'calm'],
  },
};

function bestFit<T>(items: T[], keys: string[], keyOf: (item: T) => string): T | undefined {
  for (const k of keys) {
    const hit = items.find((i) => keyOf(i) === k);
    if (hit) return hit;
  }
  return items[0];
}

export type TodayTool = {
  kind: 'Breathe' | 'Frequency' | 'Mantra' | 'Meditate';
  id: string;
  title: string;
  meta: string;
  to: string;
};

export const breathMeta = (p: BreathworkPattern) =>
  [breathPatternLabel(p.pattern), durationLabel(p.totalSeconds)].filter(Boolean).join(' · ');

export const trackMeta = (t: FrequencyTrack) =>
  [t.frequencyLabel, durationLabel(t.durationSeconds)].filter(Boolean).join(' · ');

export const mantraMeta = (m: Mantra) => `${m.repetitions} repeats`;

/** A mantra's intention as a word ("Peace"); a tile has no room for its meaning paragraph. */
export const mantraIntention = (m: Mantra) =>
  COACHING_MANTRA_INTENTION_LABELS[m.intention as CoachingMantraIntention] ?? m.intention;

export const meditationMeta = (m: Meditation) =>
  `${m.minutes} min · ${m.steps.length} ${m.steps.length === 1 ? 'step' : 'steps'}`;

/**
 * One of each tool for the day, in the design's order (breathe, listen,
 * chant, sit). With no reading (no birth details yet) every list contributes
 * its first item. A tool the member cannot open is simply absent from its list.
 */
export function pickToolsForToday(input: {
  alignment: AlignmentSnapshot | null;
  patterns: BreathworkPattern[];
  tracks: FrequencyTrack[];
  mantras: Mantra[];
  meditations: Meditation[];
}): TodayTool[] {
  const fit = input.alignment ? TOOL_FIT[softestAxis(input.alignment).key] : null;
  const tools: TodayTool[] = [];
  const breath = bestFit(input.patterns, fit?.breath ?? [], (p) => p.outcome);
  if (breath) {
    tools.push({
      kind: 'Breathe',
      id: breath.id,
      title: breath.title,
      meta: breathMeta(breath),
      to: `/align/breathwork/${encodeURIComponent(breath.id)}`,
    });
  }
  const track = bestFit(input.tracks, fit?.frequency ?? [], (t) => t.category);
  if (track) {
    tools.push({
      kind: 'Frequency',
      id: track.id,
      title: track.title,
      meta: trackMeta(track),
      to: `/align/frequencies/${track.id}`,
    });
  }
  const mantra = bestFit(input.mantras, fit?.mantra ?? [], (m) => m.intention);
  if (mantra) {
    tools.push({
      kind: 'Mantra',
      id: mantra.id,
      title: mantra.title,
      meta: mantraMeta(mantra),
      to: `/align/mantras/${encodeURIComponent(mantra.id)}`,
    });
  }
  const meditation = bestFit(input.meditations, fit?.meditation ?? [], (m) => m.intention);
  if (meditation) {
    tools.push({
      kind: 'Meditate',
      id: meditation.id,
      title: meditation.title,
      meta: meditationMeta(meditation),
      to: `/align/meditations/${encodeURIComponent(meditation.id)}`,
    });
  }
  return tools;
}

const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export type StreakDot = { day: string; letter: string; filled: boolean; today: boolean };

/**
 * The last seven days ending today, filled where the CURRENT streak covers
 * them. The streak counts back from today once today qualifies, else from
 * yesterday, so that is where the run ends. Days before the run are left
 * empty: the score read carries no older qualification, and the dots only
 * ever claim what the "N-day streak" line beneath them says.
 */
export function streakWeek(today: string, streak: StreakState): StreakDot[] {
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const runEnd = streak.todayQualified ? 0 : 1;
  return Array.from({ length: 7 }, (_, i) => {
    const daysAgo = 6 - i;
    const date = new Date(Date.UTC(y, m - 1, d - daysAgo));
    return {
      day: date.toISOString().slice(0, 10),
      letter: WEEKDAY_LETTERS[date.getUTCDay()]!,
      filled: daysAgo >= runEnd && daysAgo < runEnd + streak.current,
      today: daysAgo === 0,
    };
  });
}

const COMMUNITY_APPS: Array<[RegExp, string]> = [
  [/(^|\.)(t\.me|telegram\.(me|org))$/, 'Telegram'],
  [/(^|\.)(facebook\.com|fb\.com|fb\.me|m\.me|messenger\.com)$/, 'Facebook'],
  [/(^|\.)(viber\.com|invite\.viber\.com)$/, 'Viber'],
  [/(^|\.)(wa\.me|whatsapp\.com)$/, 'WhatsApp'],
  [/(^|\.)(discord\.gg|discord\.com)$/, 'Discord'],
];

/** Which app a community link opens, for the row's meta line; null for any other address. */
export function communityApp(url: string): string | null {
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
  return COMMUNITY_APPS.find(([pattern]) => pattern.test(host))?.[1] ?? null;
}
